import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { ScanFace, Loader2, AlertTriangle, CheckCircle2, RotateCw } from 'lucide-react';
import { albumsApi } from '../../api/albums';
import styles from './FaceIndexStatus.module.css';

interface FaceIndexStatusProps {
  albumId: string;
}

type Status = 'idle' | 'running' | 'completed' | 'failed';

interface FaceIndexState {
  enabled: boolean;
  status: Status;
  processed: number;
  /** Photos that hit a terminal failure (too_large / other). Counts as "settled"
   *  so the bar can complete instead of falsely showing "stuck". */
  failed: number;
  total: number;
  error: string | null;
  attempts: number;
  startedAt: string | null;
  completedAt: string | null;
  hasCollection: boolean;
}

const POLL_RUNNING_MS = 3_000;
const POLL_IDLE_MS = 30_000;
const MAX_AUTO_RETRIES = 5;

export function FaceIndexStatus({ albumId }: FaceIndexStatusProps) {
  const [state, setState] = useState<FaceIndexState | null>(null);
  const [retrying, setRetrying] = useState(false);
  const retryingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoRetryCountRef = useRef(0);
  const wasFailedRef = useRef(false);
  const lastProcessedRef = useRef(-1);

  const doRetry = async (isAuto = false) => {
    if (retryingRef.current) return;
    retryingRef.current = true;
    setRetrying(true);
    try {
      await albumsApi.retryFaceIndex(albumId);
      if (!isAuto) {
        toast.success('Indexing restarted');
      } else {
        toast.success(`Auto-retrying face indexing (attempt ${autoRetryCountRef.current}/${MAX_AUTO_RETRIES})`, { duration: 3000 });
      }
      wasFailedRef.current = false;
      await fetchStatus();
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? 'Could not start indexing';
      if (!isAuto) toast.error(msg);
    } finally {
      retryingRef.current = false;
      setRetrying(false);
    }
  };

  const fetchStatus = async () => {
    try {
      const data = await albumsApi.getFaceIndexStatus(albumId);

      // "Settled" = photos that reached a terminal state, indexed OR failed.
      const settled = data.processed + (data.failed ?? 0);

      // Reset auto-retry count on any forward progress (indexed or newly-failed)
      if (settled > lastProcessedRef.current && settled > 0) {
        autoRetryCountRef.current = 0;
        wasFailedRef.current = false;
      }
      lastProcessedRef.current = settled;

      // Detect failure transitions and auto-retry. A run is only "incomplete" when
      // photos remain neither indexed nor failed — i.e. the run actually died with
      // work left. too_large/other failures that fill out the remainder are NOT
      // incompleteness; auto-retrying them is wasted (they're handled elsewhere).
      const isTerminal = data.status !== 'running';
      const isIncomplete = data.total > 0 && settled < data.total;
      const isFailed = data.enabled && (data.status === 'failed' || (isTerminal && isIncomplete));

      if (isFailed && !wasFailedRef.current) {
        wasFailedRef.current = true;
        if (autoRetryCountRef.current < MAX_AUTO_RETRIES) {
          autoRetryCountRef.current += 1;
          setTimeout(() => doRetry(true), 1500);
        }
      } else if (!isFailed) {
        wasFailedRef.current = false;
      }

      setState(data);
      return data;
    } catch {
      return null;
    }
  };

  // Self-pacing poll — fast while running, slow when idle/done
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const data = await fetchStatus();
      if (cancelled) return;
      const delay = data?.status === 'running' ? POLL_RUNNING_MS : POLL_IDLE_MS;
      timerRef.current = setTimeout(tick, delay);
    };
    tick();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [albumId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!state || !state.enabled) return null;

  const { status, processed, failed, total, error } = state;
  const settled = processed + (failed ?? 0);
  // Bar fills on settled progress so a run that finished with some failures still
  // reaches 100% (and reads "Ready") rather than parking just below the line.
  const percent = total > 0 ? Math.min(100, Math.round((settled / total) * 100)) : 0;
  const remaining = Math.max(0, total - settled);

  // Surface a retry only when work is genuinely left undone — status='failed'
  // (explicit), or terminal with photos neither indexed nor failed (run died).
  // A terminal run where indexed+failed === total is complete: the failures are
  // reported as a note and recovered via the event Errors tab, not here.
  const isTerminal = status !== 'running';
  const isIncomplete = total > 0 && settled < total;
  const isFailureState = status === 'failed' || (isTerminal && isIncomplete);
  const isReadyState = isTerminal && total > 0 && settled >= total;

  type DerivedStatus = 'running' | 'failed' | 'ready' | 'empty';
  const effectiveStatus: DerivedStatus =
    status === 'running' ? 'running'
    : total === 0 ? 'empty'
    : isFailureState ? 'failed'
    : isReadyState ? 'ready'
    : 'failed';

  const handleRetry = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await doRetry(false);
  };

  const tone: 'failed' | 'running' | 'ready' | 'idle' =
    effectiveStatus === 'failed' ? 'failed'
    : effectiveStatus === 'running' ? 'running'
    : effectiveStatus === 'ready' ? 'ready'
    : 'idle';

  return (
    <div className={`${styles['block']} ${styles[`tone-${tone}`]}`} onClick={(e) => e.stopPropagation()}>
      {/* Header — icon + label + percent or status pill */}
      <div className={styles['header-row']}>
        <span className={styles['label-group']}>
          <ScanFace className={styles['icon-sm']} aria-hidden />
          <span className={styles['label-text']}>Face match</span>
        </span>

        {effectiveStatus === 'running' && (
          <span className={`${styles['pill']} ${styles['pill-running']}`}>
            <Loader2 className={`${styles['icon-xs']} ${styles['spin']}`} aria-hidden />
            Indexing
          </span>
        )}
        {effectiveStatus === 'failed' && (
          <span className={`${styles['pill']} ${styles['pill-failed']}`}>
            <AlertTriangle className={styles['icon-xs']} aria-hidden />
            Action needed
          </span>
        )}
        {effectiveStatus === 'ready' && (
          <span className={`${styles['pill']} ${styles['pill-ready']}`}>
            <CheckCircle2 className={styles['icon-xs']} aria-hidden />
            Ready
          </span>
        )}
        {effectiveStatus === 'empty' && (
          <span className={`${styles['pill']} ${styles['pill-muted']}`}>No photos</span>
        )}
      </div>

      {/* Progress numerals */}
      {total > 0 && (
        <>
          <div className={styles['count-row']}>
            <span className={styles['count-strong']}>{processed.toLocaleString()}</span>
            <span className={styles['count-divider']}>/</span>
            <span className={styles['count-total']}>{total.toLocaleString()}</span>
            <span className={styles['count-label']}>
              {remaining > 0
                ? `· ${remaining.toLocaleString()} remaining`
                : failed > 0
                  ? `indexed · ${failed.toLocaleString()} couldn't be indexed`
                  : 'photos indexed'}
            </span>
            <span className={styles['count-spacer']} />
            <span className={styles['percent']}>{percent}%</span>
          </div>

          <div className={styles['progress-track']} aria-hidden>
            <motion.div
              className={`${styles['progress-fill']} ${effectiveStatus === 'failed' ? styles['progress-fill-failed'] : ''}`}
              initial={false}
              animate={{ width: `${percent}%` }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
        </>
      )}

      {/* Error + retry */}
      {effectiveStatus === 'failed' && (
        <div className={styles['error-row']}>
          <span className={styles['error-text']} title={error ?? undefined}>
            {autoRetryCountRef.current >= MAX_AUTO_RETRIES
              ? `Auto-retry failed after ${MAX_AUTO_RETRIES} attempts`
              : error ?? 'Indexing did not complete'}
          </span>
          <button
            type="button"
            onClick={handleRetry}
            disabled={retrying}
            className={styles['retry-button']}
          >
            <RotateCw className={`${styles['icon-xs']} ${retrying ? styles['spin'] : ''}`} aria-hidden />
            {retrying ? 'Starting…' : 'Retry'}
          </button>
        </div>
      )}
    </div>
  );
}
